import React from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { DECIMAL_PRECISIONS, DecimalSeparator, ThousandSeparator } from '@makyoapp/makyo-common'
import * as Icon from '@makyoapp/makyo-icon/icon'
import { SelectDropdown, SimpleInput, SingleIconSelect, Switch } from '@makyoapp/makyo-ui'

import { useCreateColumnContext } from '@/context/CreateColumnContext'

import { Fld, Lbl } from './shared'

/**
 * handle decimal / precision field for number column type
 */
function FieldNumberDecimal() {
  const { t } = useTranslation()
  const { id, value, setValue } = useCreateColumnContext()
  return (
    <Fld>
      <Lbl>{t('Precision')}</Lbl>
      <SelectDropdown
        id={`${id}-number-decimal`}
        value={value.numberOptions?.numPrecision}
        menuContainerClassName='mk-ag-sdd '
        menuZIndex='z-305'
        portalMenu
        onChange={(decimal) => {
          setValue({
            numberOptions: {
              ...value.numberOptions,
              numPrecision: decimal,
            },
          })
        }}
        options={[...DECIMAL_PRECISIONS]}
      />
    </Fld>
  )
}

/**
 * handle decimal separator field for number column type
 */
function FieldNumberDecimalSeparator() {
  const { t } = useTranslation()
  const { id, value, setValue } = useCreateColumnContext()

  return (
    <Fld>
      <Lbl>{t('Decimal Separator')}</Lbl>
      <SingleIconSelect
        id={`${id}-number-dec-sep`}
        defaultSize={false}
        optionClassName={'w-8 h-8'}
        onClick={(e) => {
          setValue({
            numberOptions: {
              ...value.numberOptions,
              numDecimalSeparator: e.id as DecimalSeparator,
            },
          })
        }}
        options={[
          {
            id: 'apostrophe',
            icon: (selected) => {
              return (
                <Icon.Apostrophe16
                  className={clsx('group-hover:fill-white', selected ? 'fill-white' : 'fill-zinc-700')}
                />
              )
            },
            title: 'Apostrophe',
          },
          {
            id: 'dot',
            icon: (selected) => {
              return (
                <Icon.Dot16 className={clsx('group-hover:fill-white', selected ? 'fill-white' : 'fill-zinc-700')} />
              )
            },
            title: 'Dot',
          },
          {
            id: 'comma',
            icon: (selected) => {
              return (
                <Icon.Comma16 className={clsx('group-hover:fill-white', selected ? 'fill-white' : 'fill-zinc-700')} />
              )
            },
            title: 'Comma',
          },
        ]}
        selected={
          value.numberOptions.numDecimalSeparator !== undefined ? value.numberOptions.numDecimalSeparator : 'dot'
        }
      />
    </Fld>
  )
}

/**
 * handle thousand separator field for number column type
 */
function FieldNumberThousandSeparator() {
  const { t } = useTranslation()
  const { id, value, setValue } = useCreateColumnContext()
  return (
    <Fld>
      <Lbl>{t('Thousand Separator')}</Lbl>
      <SingleIconSelect
        id={`${id}-number-thou-sep`}
        defaultSize={false}
        optionClassName={'w-8 h-8'}
        className='min-w-0'
        onClick={(e) => {
          setValue({
            numberOptions: {
              ...value.numberOptions,
              numThousandSeparator: e.id as ThousandSeparator,
            },
          })
        }}
        options={[
          {
            id: 'none',
            icon: (selected) => {
              return <div className={clsx(selected ? 'bg-teal-600' : 'bg-zinc-100')} />
            },
            title: 'None',
          },
          {
            id: 'space',
            icon: (selected) => {
              return (
                <Icon.SpaceBar24
                  className={clsx('group-hover:fill-white', selected ? 'fill-white' : 'fill-zinc-700')}
                />
              )
            },
            title: 'Space',
          },
          {
            id: 'dot',
            icon: (selected) => {
              return (
                <Icon.Dot16 className={clsx('group-hover:fill-white', selected ? 'fill-white' : 'fill-zinc-700')} />
              )
            },
            title: 'Dot',
          },
          {
            id: 'comma',
            icon: (selected) => {
              return (
                <Icon.Comma16 className={clsx('group-hover:fill-white', selected ? 'fill-white' : 'fill-zinc-700')} />
              )
            },
            title: 'Comma',
          },
        ]}
        selected={
          value.numberOptions.numThousandSeparator !== undefined ? value.numberOptions.numThousandSeparator : 'comma'
        }
      />
    </Fld>
  )
}

/**
 * handle abbreviation field for number column type
 */
function FieldNumberAbbreviate() {
  const { id, value, setValue } = useCreateColumnContext()
  const { t } = useTranslation()
  return (
    <Fld>
      <Lbl>{t('Abbreviate Number')}</Lbl>
      <Switch
        id={`${id}-number-abbr`}
        checked={value.numberOptions?.numAbbreviate}
        onClick={(abbrNumber) => {
          setValue({
            numberOptions: {
              ...value.numberOptions,
              numAbbreviate: abbrNumber,
            },
          })
        }}
      />
    </Fld>
  )
}

/**
 * handle prefix field for number column type
 */
function FieldNumberPrefix() {
  const { id, value, setValue } = useCreateColumnContext()
  const { t } = useTranslation()
  return (
    <Fld>
      <Lbl>{t('Prefix')}</Lbl>
      <SimpleInput
        inputClassName='min-w-0'
        id={`${id}-number-prefix`}
        value={value.numberOptions.numPrefix}
        onChange={(e) => {
          setValue({
            numberOptions: {
              ...value.numberOptions,
              numPrefix: e.currentTarget.value,
            },
          })
        }}
      />
    </Fld>
  )
}

/**
 * handle suffix field for number column type
 */
function FieldNumberSuffix() {
  const { id, value, setValue } = useCreateColumnContext()
  const { t } = useTranslation()
  return (
    <Fld>
      <Lbl>{t('Suffix')}</Lbl>
      <SimpleInput
        id={`${id}-number-suffix`}
        inputClassName='min-w-0'
        value={value.numberOptions.numSuffix}
        onChange={(e) => {
          setValue({
            numberOptions: {
              ...value.numberOptions,
              numSuffix: e.currentTarget.value,
            },
          })
        }}
      />
    </Fld>
  )
}

/**
 * handle create column number options field
 */
export default function FieldNumber() {
  const { value } = useCreateColumnContext()

  return (
    <>
      <FieldNumberDecimal />
      <FieldNumberPrefix />
      <FieldNumberSuffix />
      {value.numberOptions.numPrecision !== '1' && <FieldNumberDecimalSeparator />}
      <FieldNumberThousandSeparator />
      <FieldNumberAbbreviate />
    </>
  )
}
